import { z } from 'zod';
import { TopUpRequestMethodSchema, TopUpRequestSchema } from './deposit-topup';
import { LedgerMethodSchema, ledgerEntrySignedAmount, } from './payment';
/**
 * Client-facing finance read models (profile «Фінанси» tab):
 * `GET /api/users/me/finance` and `GET /api/users/me/finance/transactions`.
 *
 * Buying power is NOT here — it is `GET /api/users/me/exposure` (single source).
 * The transactions list is a pure fold over the client's CHF `payments` ledger
 * rows plus the settlements of their invoices ({@link clientLedgerRows}).
 */
/** Row kind as the client sees it. `invoice_payment` never moves the deposit. */
export const ClientLedgerKindSchema = z.enum([
    'top_up',
    'prepayment',
    'refund',
    'adjustment',
    'fee',
    'invoice_payment',
    /** The deposit withheld because the client refused to pay for a won car (decision a). */
    'penalty',
]);
/** One row of the client's money movements. */
export const ClientLedgerRowSchema = z.object({
    id: z.string(),
    at: z.coerce.date(),
    kind: ClientLedgerKindSchema,
    /** How the money moved; null for legacy rows and invoice payments. */
    method: LedgerMethodSchema.nullable(),
    /** Signed CHF: + credits the client, − debits them. */
    amountChf: z.number(),
    /** Deposit balance after this row (unchanged by an `invoice_payment`). */
    balanceAfterChf: z.number(),
    /** False for an `invoice_payment` — the UI must not draw it as a deposit movement. */
    affectsDeposit: z.boolean(),
    invoiceNo: z.string().nullable(),
    lotTitle: z.string().nullable(),
    reference: z.string().nullable(),
});
export const ClientLedgerPageSchema = z.object({
    rows: z.array(ClientLedgerRowSchema),
    nextCursor: z.string().nullable(),
});
/** Whether the client may ask for their deposit back right now, and why not. */
export const RefundEligibilitySchema = z.object({
    allowed: z.boolean(),
    activeBidCount: z.number(),
    unpaidInvoiceCount: z.number(),
    depositChf: z.number(),
    /** Set while a refund request is pending. */
    requestedAt: z.coerce.date().nullable(),
    requestedAmountChf: z.number().nullable(),
    /**
     * CHF still committed by bids that stand on the real auction (a relayed bid
     * whose lot moved on). Above 0 it blocks a refund on its own, even with no
     * active bid. Optional: an API that predates the field still parses.
     */
    committedChf: z.number().optional(),
});
/** `GET /api/users/me/finance`. */
export const ClientFinanceSummarySchema = z.object({
    depositChf: z.number(),
    lastTopUp: z
        .object({
        at: z.coerce.date(),
        amountChf: z.number(),
        method: LedgerMethodSchema.nullable(),
    })
        .nullable(),
    refund: RefundEligibilitySchema,
    topUp: z.object({
        enabled: z.boolean(),
        methods: z.array(TopUpRequestMethodSchema),
        cashMaxChf: z.number().nullable(),
        minChf: z.number().nullable(),
        openRequests: z.array(TopUpRequestSchema),
        /**
         * The client's own recently REJECTED requests, newest first, each with the
         * manager's `rejectReason` — so a rejected request does not silently vanish
         * from the open list. Additive: an older API that does not send it parses
         * as an empty list.
         */
        recentRejected: z.array(TopUpRequestSchema).default([]),
    }),
    invoices: z.object({
        unpaidCount: z.number(),
        dueChf: z.number(),
    }),
});
const ENTRY_KIND = {
    deposit: 'top_up',
    prepayment: 'prepayment',
    refund: 'refund',
    adjustment: 'adjustment',
    fee: 'fee',
    penalty: 'penalty',
};
function chronological(a, b) {
    const byTime = a.at.getTime() - b.at.getTime();
    if (byTime !== 0)
        return byTime;
    if (a.id < b.id)
        return -1;
    if (a.id > b.id)
        return 1;
    return 0;
}
/**
 * The client's money movements, newest first. Pure.
 *
 * - Only CHF ledger rows count (legacy EUR/USD/UAH rows are excluded — the
 *   deposit is CHF).
 * - Ledger rows are signed with {@link ledgerEntrySignedAmount} and folded in
 *   chronological order (`at`, then `id`) into a running deposit balance;
 *   each row carries the balance after it.
 * - A settlement becomes an `invoice_payment` row: negative amount,
 *   `affectsDeposit: false`, and `balanceAfterChf` equal to the running
 *   deposit balance at that point — it never moves the balance.
 */
export function clientLedgerRows(entries, settlements) {
    const timeline = [
        ...entries
            .filter((entry) => entry.currency === 'CHF')
            .map((entry) => ({
            source: 'ledger',
            at: entry.createdAt,
            id: entry.id,
            entry,
        })),
        ...settlements.map((settlement) => ({
            source: 'settlement',
            at: settlement.at,
            id: settlement.id,
            settlement,
        })),
    ].sort(chronological);
    let balance = 0;
    const rows = timeline.map((item) => {
        if (item.source === 'settlement') {
            const { settlement } = item;
            return {
                id: settlement.id,
                at: settlement.at,
                kind: 'invoice_payment',
                method: null,
                amountChf: -settlement.amountChf,
                balanceAfterChf: balance,
                affectsDeposit: false,
                invoiceNo: settlement.invoiceNo,
                lotTitle: settlement.lotTitle ?? null,
                reference: null,
            };
        }
        const { entry } = item;
        const signed = ledgerEntrySignedAmount({
            type: entry.type,
            amount: entry.amount,
            direction: entry.direction ?? undefined,
        });
        // Round to CHF cents so a long fold of fractional amounts does not drift (0.1 + 0.2).
        balance = Math.round((balance + signed) * 100) / 100;
        return {
            id: entry.id,
            at: entry.createdAt,
            kind: ENTRY_KIND[entry.type],
            method: entry.method ?? null,
            amountChf: signed,
            balanceAfterChf: balance,
            affectsDeposit: true,
            invoiceNo: entry.invoiceNo ?? null,
            lotTitle: entry.lotTitle ?? null,
            reference: entry.reference ?? null,
        };
    });
    return rows.reverse();
}
//# sourceMappingURL=client-finance.js.map